import * as Client from "./Client";
import { logError } from "../infrastructure/TelemetryService";

// Call
// - makes the service call
// - use this for operations that may reasonably fail for more complicated entities
// - logs and wraps exceptions into the result object
// - caller is in full control of what to do for specific error scenarios
export async function call<T>(operation: () => Promise<T>): Promise<CallResult<T>> {
    try {
        const response = await withRetry(operation);
        return new CallResult<T>(response, undefined);
    } catch (ex: any) {
        const apiExceptionDetails = getApiExceptionDetails(ex);
        return new CallResult<T>(undefined, { errorType: apiExceptionDetails.errorType, unitOfWorkCorrelationId: apiExceptionDetails.unitOfWorkCorrelation });
    }
}

export enum ErrorTypeEnum {
    BadRequest400,
    NotAuthenticated401,
    NotAuthorised403,
    NotFound404,
    Conflict409,
    ServerTooBusy429,
    InternalServerError500,
    ServiceUnavailable,
    UnexpectedError,
}

export interface CallResultError {
    errorType: ErrorTypeEnum;
    unitOfWorkCorrelationId: string;
}

export class CallResult<T> {
    constructor(response: T | undefined, error: CallResultError | undefined) {
        this.Response = response;
        this.Error = error;
    }

    Response: T | undefined;
    Error: CallResultError | undefined;
}

async function withRetry<T>(operation: () => Promise<T>): Promise<T> {
    const attempts: number = 3;
    const transientErrorCodes: Array<number | undefined> = [500, undefined];
    const delay: number = 1000;
    let attempt: number = 0;

    while (true) {
        attempt++;
        try {
            // give it a crack
            return await operation();
        } catch (ex: any) {
            //console.log(JSON.stringify(ex));
            const apiExceptionDetails = getApiExceptionDetails(ex);
            console.warn(`Failed service call; attempt ${attempt} of ${attempts} with correlation '${apiExceptionDetails.unitOfWorkCorrelation}'.`);
            // always log (to appinsights)
            logError(JSON.stringify(ex));
            // all exceptions from nswag are wrapped with this type
            // intransient errors do not get retries, immediately fail
            if (!transientErrorCodes.includes(apiExceptionDetails.status)) {
                console.error("Non-transient error encountered, not retrying.");
                throw ex;
            }
            // if we're exceeded the number of attempts, fail
            if (attempt === attempts) throw ex;
            // wait before retrying
            await new Promise((r) => setTimeout(r, delay));
        }
    }
}

function getApiExceptionDetails(ex: any): { unitOfWorkCorrelation: string; status: number; errorType: ErrorTypeEnum } {
    const apiException: Client.ApiException = ex;
    const headers = apiException?.headers;

    // yes, it must be lowercase "unit-of-work-correlation"
    return {
        unitOfWorkCorrelation: headers ? headers["unit-of-work-correlation"] : "",
        status: apiException?.status,
        errorType: getErrorType(ex),
    };
}

function getErrorType(ex: Client.ApiException): ErrorTypeEnum {
    switch (ex.status) {
        case undefined: // was unable to fetch (i.e. the endpoint was inaccessible)
            return ErrorTypeEnum.ServiceUnavailable;
        case 500: // internal server error
            return ErrorTypeEnum.InternalServerError500;
        case 401: // not authenticated
            return ErrorTypeEnum.NotAuthenticated401;
        case 403: // unauthorised
            return ErrorTypeEnum.NotAuthorised403;
        case 400: // bad request
            return ErrorTypeEnum.BadRequest400;
        case 404: // not found
            return ErrorTypeEnum.NotFound404;
        case 409: // conflict
            return ErrorTypeEnum.Conflict409;
        case 429: // too busy
            return ErrorTypeEnum.ServerTooBusy429;
        default:
            // unknown
            return ErrorTypeEnum.UnexpectedError;
    }
}
